import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import 'date-fns';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { STAND_IN_CR } from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import useAuth from '@/hooks/useAuth';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import { setAffectedVehiclesVolumes, setCampaignVehicles } from '@/redux/Campaign/campaign.action';
import { getCampaignVehicles, getCampaignInfo, getLimitationPlants, getCampaignVehiclesVolumes } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { AffectedVehicles, CampaignInfo, Plant, Volume } from '@/types';
import ModelSeriesAccordions from './ModelSeriesAccordions';
import { getMSNotSelected, updateVolumesChangingTimeZone, vehicleGroupEmpty } from './VehicleCleanDirt-utils';
import styles from './VehicleCleanDirt.module.scss';
import stylesTabs from '../VolumeDetails.module.scss';

interface VehicleCleanDirtProps {
  campaignInfo: CampaignInfo;
  plantSelected: Plant;
  headerLanguage: string;
  defaultLanguage: string;
  timeZone: string;
  affectedVehicles: AffectedVehicles;
  setAffectedVehicles: (affectedVehicles: AffectedVehicles) => void;
  limitationPlants: Plant[];
  volumes: Volume[];
  setVolumes: (volumes: Volume[]) => void;
}

const VehicleCleanDirt: React.FC<VehicleCleanDirtProps> = (props) => {
  const { campaignInfo, plantSelected, headerLanguage, defaultLanguage, timeZone, affectedVehicles, setAffectedVehicles,
    limitationPlants, volumes, setVolumes } = props;
  const [timeZoneChange, setTimeZoneChange] = useState(false);
  const authService = useAuth();
  const { t } = useTranslation();
  const { isButtonDisabled, isReadOnly } = useCRPermissionMatrix(campaignInfo.status, campaignInfo.authorization);
  const isUserStandin = authService.userCode && campaignInfo?.recipients?.[authService.userCode] === STAND_IN_CR;
  const checkDisabled = !isUserStandin && (isReadOnly() || isButtonDisabled(CR_PERMISSION_ACTIONS.SAVE) || (headerLanguage !== defaultLanguage));

  useEffect(() => {
    if (timeZoneChange) {
      updateVolumesChangingTimeZone(affectedVehicles, setAffectedVehicles, timeZone);
    }
    setTimeZoneChange(true);
  }, [timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasAllMSSelected = useMemo(() => !getMSNotSelected(volumes, limitationPlants, plantSelected)?.length,
    [affectedVehicles?.volumes, limitationPlants, plantSelected?.wkzNumber]); // eslint-disable-line

  const handleOnClickAddGroup = () => {
    const newVolumes = volumes.map((vol) => (vol.plant === plantSelected.wkzNumber
      ? {
        ...vol,
        limitationVolumeManualEntry: {
          ...vol.limitationVolumeManualEntry,
          // @ts-ignore:next-line //TODO: parse VehicleCleanDirt-utils.js to TS
          vehicleGroups: vol.limitationVolumeManualEntry.vehicleGroups?.concat(vehicleGroupEmpty),
        },
      }
      : vol));
    setVolumes(newVolumes);
  };

  return (
    <div className={stylesTabs.TabContainer}>
      {hasAllMSSelected && <InfoMsg msg={t('VehicleCleanDirt.allMSSelected')} customClass="w-100" />}
      <div className={styles.Header}>
        <h4>{t('VehicleCleanDirt.cleanDirtCriteria')}</h4>
        <SimpleButton
          id="btn-add-vehicle-group"
          color="primary"
          buttonText={t('VehicleCleanDirt.addVehicleGroup')}
          disabled={hasAllMSSelected}
          handleClick={handleOnClickAddGroup}
        />
      </div>
      <div className={styles.Accordions}>
        <ModelSeriesAccordions disabled={checkDisabled} plantSelected={plantSelected} hasAllMSSelected={hasAllMSSelected} />
      </div>
    </div>
  );
};

const actionCreators = {
  setAffectedVehicles: setCampaignVehicles,
  setVolumes: setAffectedVehiclesVolumes,
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
  affectedVehicles: getCampaignVehicles(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  defaultLanguage: getCampaignInfo(state)?.defaultLanguage,
  timeZone: getSettingsInfo(state)?.timezone,
  limitationPlants: getLimitationPlants(state),
  volumes: getCampaignVehiclesVolumes(state),
});

export default connect(mapStateToProps, actionCreators)(VehicleCleanDirt);
